import { useEffect, useMemo, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AuthErrorHeader } from './AuthHeader';
import { useAnalytics } from 'src/AnalyticsContext';
import sageLogo from 'src/assets/images/sage-logo-on-light.svg';
import { useSageAuth } from 'src/auth/useSageAuth';
import SageLoader from 'src/components/fragments/SageLoader';
import { isFetchBaseQueryError } from 'src/store/rtkHelpers';
import {
    IdentityProviderConnection,
    useGetIdentityProviderConnectionsQuery,
} from 'src/store/sageIdentityProviderSyncApi';
import { snackActions } from 'src/utils/snackBarTools';

interface SSODirectLoginProps {
    domain: string;
    onRedirectToPhoneNumberLogin: () => void;
}

type SamlProvider = NonNullable<IdentityProviderConnection['samlProvider']>;

interface SamlProviderContent {
    buttonLabel: string;
    accountDescription: string;
    buttonIcon?: IconDefinition;
}

const SamlProviderContents: Record<SamlProvider, SamlProviderContent> = {
    ENTRA: {
        buttonLabel: 'Continue with Microsoft',
        accountDescription: 'Microsoft account',
        buttonIcon: faMicrosoft,
    },
    GENERIC_SAML: {
        buttonLabel: 'Continue with SSO',
        accountDescription: 'SSO provider',
    },
};

const PageName = 'SSO Direct Login page';

export default function SSODirectLogin(props: SSODirectLoginProps) {
    const { domain, onRedirectToPhoneNumberLogin } = props;

    const { login } = useSageAuth();
    const { spacing, zIndex } = useTheme();
    const { trackEvent, trackPageView } = useAnalytics();

    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const {
        data: connection,
        error,
        isLoading,
    } = useGetIdentityProviderConnectionsQuery({ domain });

    const { accountDescription, buttonIcon, buttonLabel } = useMemo(() => {
        if (connection?.samlProvider && SamlProviderContents[connection.samlProvider]) {
            return SamlProviderContents[connection.samlProvider];
        } else {
            return SamlProviderContents.GENERIC_SAML;
        }
    }, [connection]);

    const errorPayload = useMemo(() => {
        if (isFetchBaseQueryError(error) && error?.status === 404) {
            return {
                title: 'Single Sign-On is not supported for your organization',
                caption: 'Contact support, or sign in another way.',
            };
        } else {
            return {
                title: 'Unable to find your organization',
                caption: 'Try again later, or sign in another way.',
            };
        }
    }, [error]);

    const handleLogin = async () => {
        if (!connection) {
            return;
        }

        trackEvent('Log In With SSO Clicked', { pageName: PageName });
        setIsLoggingIn(true);
        try {
            await login({
                connection: connection.auth0ConnectionName,
                forceLogin: true,
            });
        } catch (loginError) {
            datadogRum.addError(loginError, { context: 'ssoDirectLogin' });
            snackActions.error('Single Sign-On did not complete. Please try again.');
            setIsLoggingIn(false);
        }
    };

    const handleRedirectToPhoneNumberLogin = () => {
        trackEvent('Sign In Another Way Clicked', { pageName: PageName });
        onRedirectToPhoneNumberLogin();
    };

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box component="main" sx={{ minHeight: '100dvh' }}>
            {isLoading && <SageLoader />}
            {!isLoading && (
                <>
                    {error && (
                        <Box sx={{ position: 'sticky', top: 0, zIndex: zIndex.modal }}>
                            <AuthErrorHeader {...errorPayload} />
                        </Box>
                    )}
                    <Stack sx={{ gap: 4, p: 2.5 }}>
                        <Stack sx={{ gap: 2 }}>
                            <Box
                                alt="Sage"
                                component="img"
                                src={sageLogo}
                                sx={{ display: 'block', width: spacing(14), objectFit: 'contain' }}
                            />
                            <Stack sx={{ gap: 1 }}>
                                <Typography component="h1" variant="h2" sx={{ fontWeight: 'bold' }}>
                                    Welcome to Sage!
                                </Typography>
                                <Typography variant="body2" component="p">
                                    Sign in with your organization’s {accountDescription} to
                                    continue.
                                </Typography>
                            </Stack>
                        </Stack>

                        <Stack sx={{ gap: 3 }}>
                            <Button
                                disabled={!connection || isLoggingIn}
                                fullWidth
                                loading={isLoggingIn}
                                onClick={handleLogin}
                                startIcon={
                                    buttonIcon && <FontAwesomeIcon icon={buttonIcon} size="sm" />
                                }
                                variant="outlined"
                            >
                                {buttonLabel}
                            </Button>

                            <Button
                                onClick={handleRedirectToPhoneNumberLogin}
                                startIcon={
                                    buttonIcon && <FontAwesomeIcon icon={faChevronLeft} size="sm" />
                                }
                                variant="text"
                            >
                                Sign in another way
                            </Button>
                        </Stack>
                    </Stack>
                </>
            )}
        </Box>
    );
}
